import { analytics } from '@/firebaseConfig/firebaseConfig';
/** Reports page, showing metadata for available reports for the current user */
import { IonCol, IonContent, IonGrid, IonHeader, IonPage, IonRow, IonSearchbar, IonToolbar } from '@ionic/react';
import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';

import styles from '@/pages/ReportList/reportList.module.css';

import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { CustomLoading } from '@/components/Loading';
import { Loading } from '@/components/Loading/Loading';
import { NoInternet } from '@/components/NoInternet/NoInternet';
import { ReportCardList } from '@/components/ReportList';
import { toast } from '@/components/Toaster/Toast';

import { mergeClassNames } from '@/utils/mergeClassNames';

import UserService from '@/hooks/useClients/userService';
import { useIsOnline } from '@/hooks/useIsOnline';
import { useSearch } from '@/hooks/useSearch';

import { useAuthContext } from '@/contexts/authContext';
import { useReportContext } from '@/contexts/reportContext';

import { ExportedClient, ReportInfo, ReportInfoDict } from '@/models';

/** Map over reports sorted by uploadTimestamp */
const sortReportInfos = (reportInfoDict: ReportInfoDict): ReportInfo[] => {
  // Create array of tuples (id, reportInfo)
  const items: [string, ReportInfo][] = Object.keys(reportInfoDict).map((id: string) => {
    return [id, reportInfoDict[id]];
  });

  // Sort the array based on the second element (reportInfo.uploadTimestamp)
  const reportsSorted = items.sort((first, second) => {
    const a = second[1].uploadTimestamp.getTime();
    const b = first[1].uploadTimestamp.getTime();
    return a - b;
  });

  return reportsSorted.map(([_id, reportInfo]) => reportInfo);
};

type ReportsPageProps = {
  filteroption?: string;
};
/** Reports page, showing all reports (metadata) available for the current user
 * Loading reportInfo data from the database using the useReportInfo hook.
 */
export const ReportsPage = ({ filteroption }: ReportsPageProps) => {
  const historyHook = useHistory();
  const { reportInfoDict, isLoading, isError, clearError } = useReportContext();
  const isOnline = useIsOnline();

  useEffect(() => {
    console.log('ReportInfoDict updated:', reportInfoDict);
  }, [reportInfoDict]);

  useEffect(() => {
    if (isError) {
      toast({
        variant: 'error',
        title: 'Error fetching reports',
        description: isError.message,
      });
    }
  }, [isError]);

  const [searchOptions, setSearchOptions] = useState({
    includeScore: true,
    useExtendedSearch: true,
    keys: ['filename', 'tags', 'reportType'],
  });

  const list = useMemo(() => sortReportInfos(reportInfoDict), [reportInfoDict]);
  const { onSearch, query, searchResult } = useSearch(searchOptions, list);

  const handleSearch = (e: any) => {
    localStorage.setItem('search', e.target.value);
    setSearchOptions({
      includeScore: true,
      useExtendedSearch: true,
      keys: ['filename', 'tags', 'reportType'],
    });
    onSearch(e);
  };

  useEffect(() => {
    const localStorageSearch = localStorage.getItem('search');
    if (history.state.state !== undefined) {
      analytics.logEvent('search_report');
      try {
        setSearchOptions({
          includeScore: true,
          useExtendedSearch: true,
          keys: ['reportType'],
        });
        onSearch(history.state.state['filterOption']);
        localStorage.setItem('search', history.state.state['filterOption']);
      } catch {
        if (
          localStorageSearch === '1' ||
          localStorageSearch === '2' ||
          localStorageSearch === '3' ||
          localStorageSearch === '4'
        ) {
          setSearchOptions({
            includeScore: true,
            useExtendedSearch: true,
            keys: ['reportType'],
          });
          const filterOption = Number(localStorageSearch);
          onSearch(filterOption);
        } else {
          setSearchOptions({
            includeScore: true,
            useExtendedSearch: true,
            keys: ['filename', 'tags', 'reportType'],
          });
          onSearch(localStorageSearch);
        }
      }
    }
  }, [history]);

  return (
    <IonPage>
      <IonHeader className={mergeClassNames(styles.header, 'ion-no-border')} color='light'>
        <IonToolbar className={styles.toolbar}>
          <IonGrid className='ion-padding' fixed>
            <IonRow className='ion-align-items-center'>
              <IonCol class='ion-no-padding ion-align-self-center'>
                <Button
                  onClick={() => {
                    historyHook.push('/upload');
                  }}
                  size='medium'
                  variant='primary'>
                  Upload Files
                </Button>
              </IonCol>

              <IonCol className='ion-align-self-center'>
                <Input
                  isSearch
                  leadingIcon='search'
                  onIonInput={(e) => handleSearch(e)}
                  placeholder='Search...'
                  value={query}
                  variant='secondary'></Input>
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonToolbar>
      </IonHeader>
      <IonContent className={styles.content} color='light' id='main'>
        <IonGrid fixed style={{ height: '100%' }}>
          <IonRow className='ion-justify-content-center ion-align-items-center' style={{ height: '100%' }}>
            {!isOnline ? (
              <div className='ion-padding-top'>
                <NoInternet
                  description='We could not load your data, make sure you have a stable internet connection'
                  title='You are currently offline'
                />
              </div>
            ) : isLoading ? (
              <CustomLoading
                iconClassName={mergeClassNames(styles.icon, styles.spin)}
                leadingIcon='spinner'
                message='Loading reports, please wait...'
                size='medium'
              />
            ) : (
              <ReportCardList data={reportInfoDict} searchResult={searchResult} />
            )}
          </IonRow>
        </IonGrid>
      </IonContent>
    </IonPage>
  );
};
